import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { SwitchTransition } from 'react-transition-group';
import CustomizeOption from '../components/CustomizeOption'
import CustomizeModal from '../components/CustomizeModal';
import SearchInput from '../../mainFilter/filters/Destination/SearchInput';
import useGetFilteredByWhatDestinations from '../../mainFilter/filters/Destination/useGetFilteredByWhatDestinations';
import OptionsList from './Where/OptionsList';
import { t } from '../../../utilities/translator';
import FadeTransition from '../../../components/animations/FadeTransition';
import SearchList from '../../mainFilter/filters/Destination/SearchList';
import { pushEvent } from '../../../utilities/analytics';
import { CUSTOMIZE_WHERE_CLICKED, CUSTOMIZE_WHERE_SAVED } from '../../../constants/analyticEvents';
import { CUSTOMIZE } from '../../../constants/constants';
import { toggleCustomizePanel } from '../customizeButtonSlice';
import Tag from '../../../components/ui/tag';

const Where = ({
  onSave,
  selected,
  whatSelection,
}) => {
  const dispatch = useDispatch();
  const { activePanel } = useSelector(state => state.customize);
  const [searchValue, setSearchValue] = useState('');
  const {
    destinations,
    setDestinations,
    destinationsGroups,
    setDestinationsGroups,
  } = useGetFilteredByWhatDestinations(whatSelection);
  const [selectedDestinations, setSelectedDestinations] = useState(selected.destinations);
  const [selectedDestinationGroups, setSelectedDestinationGroups] = useState(selected.destinationGroups);

  useEffect(() => {
    setSelectedDestinations(selected.destinations);
    setSelectedDestinationGroups(selected.destinationGroups);
  }, [selected])

  const getOptionLabel = () => {
    const destinationsLabel = destinations.filter(item => selected.destinations.includes(item.id)).map(item => item.name);
    const destinationGroupsLabel = destinationsGroups.filter(item => selected.destinationGroups.includes(item.id)).map(item => item.name);
    return [...destinationsLabel, ...destinationGroupsLabel].join(', ') || t('mainFilter.everywhere');
  }

  const handleOptionClick = () => {
    dispatch(toggleCustomizePanel(CUSTOMIZE.WHERE));
    pushEvent(CUSTOMIZE_WHERE_CLICKED);
  }

  const handleClose = () => {
    dispatch(toggleCustomizePanel(null));
    setSelectedDestinations(selected.destinations);
    setSelectedDestinationGroups(selected.destinationGroups);
  }

  const handleClear = () => {
    dispatch(toggleCustomizePanel(null));
    onSave({
      destinations: [],
      destinationGroups: [],
    })
  }

  const handleSave = () => {
    dispatch(toggleCustomizePanel(null));
    onSave({
      destinations: selectedDestinations,
      destinationGroups: selectedDestinationGroups,
    });
    pushEvent(CUSTOMIZE_WHERE_SAVED);
  }

  const handleSearchChange = useCallback((e) => {
    setSearchValue(e.target.value);
  }, []);

  const handleToggleDestination = (optionId) => {
    let newDestinations;
    if (selectedDestinations.includes(optionId)) {
      newDestinations = selectedDestinations.filter((id) => id !== optionId);
      const currentDestination = destinations.find((item) => item.id === optionId);
      const currentDestinationGroups = destinationsGroups
        .filter((item) => currentDestination.destinations_groups.some((groupId) => groupId === item.id))
        .map(({ id }) => id);

      setSelectedDestinationGroups(selectedDestinationGroups.filter((id) => !currentDestinationGroups.includes(id)));
    } else {
      newDestinations = [...selectedDestinations, optionId];
    }

    setSelectedDestinations(newDestinations);
  };

  const handleToggleDestinationGroup = (optionId) => {
    let newGroups;
    if (selectedDestinationGroups.includes(optionId)) {
      newGroups = selectedDestinationGroups.filter(id => id !== optionId)
    } else {
      newGroups = [...selectedDestinationGroups, optionId];
    }

    const groupDestinations = destinations.filter(destination =>
      destination.destinations_groups?.some(id => newGroups.includes(id))
    );

    setSelectedDestinationGroups(newGroups);
    setSelectedDestinations(groupDestinations.map(({ id }) => id));
  }

  const handleSelectSearchOption = (id, x, y, group) => {
    if (group && !selectedDestinationGroups.includes(id)) {
      handleToggleDestinationGroup(id);
    }
    if (!group && !selectedDestinations.includes(id)) {
      handleToggleDestination(id);
    }
  }

  return (
    <>
      <CustomizeOption
        label={t('mainFilter.where')}
        value={getOptionLabel()}
        icon="ri-map-pin-2-line"
        onClick={handleOptionClick}
      />

      <CustomizeModal show={activePanel === CUSTOMIZE.WHERE} close={handleClose}>
        <CustomizeModal.Header close={handleClose} />

        <CustomizeModal.Body>
          <div className="text-xl font-semibold text-start">{t('mainFilter.where')}</div>
          <SearchInput
            placeholder="holidayFinder.destinations.searchPlaceholder"
            onChange={handleSearchChange}
            value={searchValue}
          />
          <SwitchTransition mode="out-in">
            <FadeTransition key={!!searchValue}>
              {searchValue ? (
                <SearchList
                  searchValue={searchValue}
                  destinations={destinations}
                  destinationsGroups={destinationsGroups}
                  setSearchValue={setSearchValue}
                  setDestinationsGroups={setDestinationsGroups}
                  setDestinations={setDestinations}
                  handleCheckboxChange={handleSelectSearchOption}
                />
              ) : (
                <div className="panel-flex-list gap-7">
                  {!searchValue && (
                    <div className="move-tag-list gap-2">
                      {destinations.filter(({ id }) => selectedDestinations.includes(id)).map(destination => (
                        <Tag
                          key={destination.id}
                          type="engine-standard"
                          deletable
                          label={destination.name}
                          onDelete={() => handleToggleDestination(destination.id)}
                        />
                      ))}
                    </div>
                  )}
                  <div>
                    <OptionsList
                      selectedIds={selectedDestinationGroups}
                      list={destinationsGroups}
                      title={t('destinations.themes')}
                      onChange={handleToggleDestinationGroup}
                      className="mb-10"
                    />
                    <OptionsList
                      selectedIds={selectedDestinations}
                      list={destinations}
                      title={t('destinations.favorite')}
                      onChange={handleToggleDestination}
                    />
                  </div>
                </div>
              )}
            </FadeTransition>
          </SwitchTransition>
        </CustomizeModal.Body>

        <CustomizeModal.Footer
          close={handleClose}
          onClear={handleClear}
          onSave={handleSave}
        />
      </CustomizeModal>
    </>
  )
}

export default Where
